import * as React from 'react';
import { cn } from '@/libs/cn.ts';

import { RadioGroupItem } from './radio-group-item/radio-group-item.tsx';
import './radio-group.css';

interface RadioGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  choices: Array<{
    key: string;
    value: string;
    disabled: boolean;
    kind?: React.ComponentProps<typeof RadioGroupItem>['kind'];
  }>;
  group: string;
  value?: string;
  size?: 'small' | 'medium' | 'large';
  orientation?: 'row' | 'column';
  title?: string;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  boxedLayout?: boolean;
  error?: boolean;
}

const RadioGroup = React.forwardRef<HTMLDivElement, RadioGroupProps>(
  (
    {
      choices,
      group,
      size = 'small',
      orientation = 'column',
      value,
      onBlur,
      onChange,
      title,
      error,
      boxedLayout = true,
      ...props
    },
    ref,
  ) => {
    const containerClasses = cn('radio-group__container', {
      'radio-group__container--boxed': boxedLayout,
      'radio-group__container--error': error,
    });

    const labelClasses = cn('radio-group__label', {
      'radio-group__label--boxed': boxedLayout,
      'radio-group__label--error': error,
    });

    const radioGroupClasses = cn('radio-group', {
      'radio-group--boxed': boxedLayout,
      [`radio-group-${orientation}-${size}`]: orientation && size,
    });

    return (
      <div className={containerClasses}>
        {title !== undefined && title !== '' && boxedLayout ? (
          <h3 className={labelClasses}>{title}</h3>
        ) : null}
        <div className={radioGroupClasses} ref={ref} {...props}>
          {title !== undefined && title !== '' && !boxedLayout ? (
            <h3 className={labelClasses}>{title}</h3>
          ) : null}
          {choices.map(choice => {
            return (
              <RadioGroupItem
                key={choice.key}
                id={choice.key}
                label={boxedLayout ? choice.key : undefined}
                value={choice.value}
                scale={size}
                group={group}
                disabled={choice.disabled}
                checked={choice.value === value}
                onBlur={onBlur}
                onChange={onChange}
                kind={choice.kind}
              />
            );
          })}
        </div>
      </div>
    );
  },
);

export { RadioGroup };
